/* ============================================================================
 * Printibly Help Centre — Client JS (v2 — design-integrated)
 * ----------------------------------------------------------------------------
 * Works with the .hc design system.
 * Icons hydrated by Lucide library (loaded separately via CDN in template).
 *
 * Features:
 *   - Predictive search (Shopify Predictive Search API) → .hc-typeahead dropdown
 *   - Table of Contents generation from article H2 → .hc-toc__list
 *   - Sticky header search reveal on scroll past hero
 *   - "Was this helpful?" feedback tracking
 *   - Category article counts from blogs/help.json
 *   - Printicom chat surface intent (window.PrintiblyHelp API)
 *   - Search "clear" button hydration
 *   - Mark highlights in search results
 * ============================================================================ */

(function () {
  'use strict';

  // --- ICON HYDRATION -----------------------------------------------------
  // help-icons.js (loaded separately) exposes window.HelpIcons.createIcons()
  function hydrateIcons(scope) {
    if (window.HelpIcons && typeof window.HelpIcons.createIcons === 'function') {
      window.HelpIcons.createIcons(scope);
      return;
    }
    // Retry once help-icons.js loads
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.HelpIcons) {
        window.HelpIcons.createIcons(scope);
        clearInterval(interval);
      } else if (attempts > 20) {
        clearInterval(interval);
        console.warn('[help-centre] help-icons.js failed to load');
      }
    }, 100);
  }

  // --- HELP-SCOPED SEARCH (client-side, all 11 articles cached) ----------
  // Loads /blogs/help.json once on first input, then filters in-memory.
  // Form submit is hijacked → stays on page, never punts to /search.
  let helpArticleCache = null;
  let cacheLoadPromise = null;
  let searchDebounceTimer = null;

  async function loadHelpArticles() {
    if (helpArticleCache) return helpArticleCache;
    // Server-rendered article index — Liquid emits this in layout/help.liquid
    const dataEl = document.getElementById('help-articles-data');
    if (dataEl) {
      try {
        const raw = JSON.parse(dataEl.textContent);
        helpArticleCache = raw.map((a) => ({
          title: a.title,
          handle: a.handle,
          url: a.url || `/blogs/help/articles/${a.handle}`,
          summary: a.summary || '',
          tags: a.tags || [],
          haystack: (a.title + ' ' + (a.summary || '') + ' ' + (a.tags || []).join(' ')).toLowerCase()
        }));
        return helpArticleCache;
      } catch (e) {
        console.warn('[help-centre] failed to parse embedded article index', e);
      }
    }
    helpArticleCache = [];
    return helpArticleCache;
  }

  // Synonym map — query terms on the LEFT, expanded matching terms on the RIGHT.
  // Australian + casual vocab maps to canonical article language.
  const SEARCH_SYNONYMS = {
    'post': 'shipping postage delivery dispatch',
    'postage': 'shipping postage delivery dispatch',
    'mail': 'shipping postage delivery',
    'send': 'shipping delivery dispatch',
    'deliver': 'shipping delivery dispatch',
    'delivery': 'shipping delivery dispatch carrier',
    'ship': 'shipping delivery dispatch',
    'tracking': 'tracking shipping delivery carrier',
    'track': 'tracking shipping delivery',
    'when': 'turnaround shipping delivery dispatch',
    'fast': 'rush lightning dispatch turnaround express',
    'rush': 'rush lightning dispatch turnaround express',
    'express': 'rush lightning express shipping',
    'urgent': 'rush lightning express',
    'quick': 'rush lightning express turnaround',
    'lightning': 'rush lightning express turnaround',
    'cheap': 'discount bulk pricing',
    'discount': 'discount bulk pricing tiers',
    'bulk': 'bulk discount pricing tiers wholesale',
    'wholesale': 'bulk discount pricing',
    'beat': 'price beat guarantee',
    'price': 'price pricing beat',
    'quote': 'price beat guarantee bulk',
    'minimum': 'minimum order quantity',
    'min': 'minimum order quantity',
    'one': 'minimum single order',
    'single': 'minimum single one',
    'byo': 'byo bring your own garments',
    'own': 'byo bring your own garments',
    'mine': 'byo bring your own',
    'supply': 'byo bring your own',
    'brand': 'brands stock nike adidas as colour syzmik',
    'nike': 'brands nike',
    'adidas': 'brands adidas',
    'colour': 'brands as colour design',
    'as': 'brands as colour',
    'syzmik': 'brands syzmik workwear',
    'workwear': 'workwear hi-vis syzmik brands',
    'jb': 'brands jbs jb wear',
    'hivis': 'hi-vis high visibility workwear',
    'hi-vis': 'hi-vis high visibility workwear',
    'visibility': 'hi-vis high visibility workwear',
    'safety': 'hi-vis workwear safety',
    'fluoro': 'hi-vis fluoro workwear',
    'reflective': 'hi-vis reflective workwear',
    'school': 'school leavers cohort uniform',
    'leavers': 'school leavers year 12',
    'year12': 'school leavers year 12',
    'graduation': 'school leavers',
    'uniform': 'corporate uniform staff school',
    'staff': 'corporate uniform staff',
    'team': 'team sports club uniform jersey',
    'club': 'team sports club',
    'sports': 'team sports club jersey',
    'artwork': 'artwork file print-ready design upload',
    'file': 'artwork file print-ready design upload formats',
    'design': 'artwork file design studio upload print-ready',
    'logo': 'artwork file logo design upload',
    'png': 'artwork file png transparent vector',
    'jpg': 'artwork file jpg image',
    'jpeg': 'artwork file jpg image',
    'pdf': 'artwork file pdf vector',
    'svg': 'artwork file svg vector',
    'vector': 'artwork file vector svg ai eps pdf',
    'raster': 'artwork file raster png jpg',
    'transparent': 'transparent png artwork background',
    'background': 'background transparent png artwork',
    'studio': 'design studio interactive',
    'upload': 'easy upload artwork file',
    'wash': 'care wash durability dtf',
    'care': 'care wash durability dtf print',
    'durability': 'durability wash care dtf print',
    'crack': 'durability crack peel fade dtf print',
    'peel': 'durability crack peel fade',
    'fade': 'durability fade dtf print',
    'iron': 'care iron wash dtf',
    'mockup': 'mockup proof approval',
    'proof': 'mockup proof approval',
    'approve': 'mockup proof approval',
    'approval': 'mockup proof approval',
    'change': 'mockup proof approval changes',
    'dtf': 'dtf print method',
    'dtg': 'dtg print method cotton',
    'print': 'print dtf dtg method',
    'method': 'dtf dtg method print',
    'embroidery': 'embroidery no embroidery not offered',
    'htv': 'htv heat transfer vinyl no not offered',
    'sublimation': 'sublimation no not offered',
    'reorder': 'reorder reprint repeat order',
    'reprint': 'reprint reorder repeat',
    'cancel': 'cancel refund',
    'refund': 'refund cancel return',
    'return': 'return refund quality',
  };

  function expandQuery(needle) {
    const words = needle.toLowerCase().split(/[\s-]+/).filter(Boolean);
    const expanded = new Set(words);
    for (const w of words) {
      if (SEARCH_SYNONYMS[w]) {
        SEARCH_SYNONYMS[w].split(/\s+/).forEach((syn) => expanded.add(syn));
      }
      // Also try prefix matches against the synonym keys (so "ship" matches "shipping" lookup)
      for (const key of Object.keys(SEARCH_SYNONYMS)) {
        if (key.startsWith(w) && key !== w) {
          SEARCH_SYNONYMS[key].split(/\s+/).forEach((syn) => expanded.add(syn));
        }
      }
    }
    return Array.from(expanded);
  }

  function clientFilter(articles, q) {
    const needle = q.toLowerCase().trim();
    if (!needle) return [];
    const expandedTerms = expandQuery(needle);

    return articles
      .map((a) => {
        let score = 0;
        const t = a.title.toLowerCase();
        // Exact / substring match on original query (highest weight)
        if (t === needle) score += 100;
        else if (t.includes(needle)) score += 50;

        // Each expanded term contributes — direct matches outweigh synonym hits
        for (const w of expandedTerms) {
          if (t.includes(w)) score += 10;
          if (a.tags.some((tag) => tag.toLowerCase().includes(w))) score += 6;
          if (a.summary.toLowerCase().includes(w)) score += 4;
          if (a.haystack.includes(w)) score += 1;
        }
        return { a, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((x) => x.a);
  }

  function setupPredictiveSearch() {
    // Preload articles on first user interaction (idle / mouseover the page)
    const preload = () => { loadHelpArticles(); };
    document.addEventListener('mousemove', preload, { once: true, passive: true });
    document.addEventListener('keydown', preload, { once: true, passive: true });
    setTimeout(preload, 1500); // fallback — load on idle

    document.querySelectorAll('[data-help-search]').forEach(setupSearchForm);

    // Sticky header search (no surrounding [data-help-search] wrapper)
    const stickyInput = document.querySelector('[data-help-sticky-search-input]');
    if (stickyInput) {
      const stickyResults = document.querySelector('[data-help-sticky-search-results]');
      const stickyForm = stickyInput.closest('form');
      if (stickyForm) {
        stickyForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const q = stickyInput.value.trim();
          if (!q) return;
          // navigate to /pages/help?q=… (stays in help chrome)
          window.location.href = `/pages/help?q=${encodeURIComponent(q)}`;
        });
        bindSearchInput(stickyInput, stickyResults, stickyInput.closest('.hc-search'));
        document.addEventListener('click', (e) => {
          if (!stickyForm.contains(e.target) && stickyResults) stickyResults.hidden = true;
        });
      }
    }

    // If URL has ?q=, run a search on page load (lets users land here with a query)
    const params = new URLSearchParams(window.location.search);
    const initialQ = (params.get('q') || '').trim();
    if (initialQ) runFullSearchView(initialQ);
  }

  function setupSearchForm(form) {
    const input = form.querySelector('[data-help-search-input]');
    const results = form.querySelector('[data-help-search-results]');
    if (!input) return;

    const searchEl = input.closest('.hc-search');

    // Hijack submit — never let it go to /search
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      runFullSearchView(q);
      results && (results.hidden = true);
    });

    bindSearchInput(input, results, searchEl);

    document.addEventListener('click', (e) => {
      if (!form.contains(e.target) && results) {
        results.hidden = true;
        if (searchEl) searchEl.classList.remove('is-focus');
      }
    });
    input.addEventListener('focus', () => searchEl && searchEl.classList.add('is-focus'));
  }

  function bindSearchInput(input, resultsEl, searchEl) {
    input.addEventListener('input', async (e) => {
      const q = (e.target.value || '').trim();
      clearTimeout(searchDebounceTimer);
      if (q.length < 2) {
        if (resultsEl) {
          resultsEl.hidden = true;
          resultsEl.innerHTML = '';
        }
        return;
      }
      searchDebounceTimer = setTimeout(async () => {
        const articles = await loadHelpArticles();
        const matches = clientFilter(articles, q);
        renderTypeahead(matches, resultsEl, q);
      }, 100);
    });
    input.addEventListener('focus', () => searchEl && searchEl.classList.add('is-focus'));
  }

  function renderTypeahead(matches, resultsEl, q) {
    if (!resultsEl) return;
    if (!matches.length) {
      resultsEl.innerHTML = `<div class="hc-ta__empty">No help articles match "${escapeHtml(q)}".</div>`;
      resultsEl.hidden = false;
      return;
    }
    resultsEl.innerHTML = `
      <div class="hc-ta__group">
        <div class="hc-ta__label">Articles</div>
        ${matches.slice(0, 6).map((a) => `
          <a class="hc-ta__item" href="${escapeHtml(a.url)}">
            <i data-lucide="file-text"></i>
            <span>
              <b>${highlightMatch(a.title, q)}</b>
              ${a.summary ? `<span>${escapeHtml(a.summary.slice(0, 80))}</span>` : ''}
            </span>
          </a>
        `).join('')}
      </div>
    `;
    resultsEl.hidden = false;
    hydrateIcons(resultsEl);
  }

  function highlightMatch(text, q) {
    const needle = q.toLowerCase();
    const lower = text.toLowerCase();
    const idx = lower.indexOf(needle);
    if (idx < 0) return escapeHtml(text);
    return escapeHtml(text.slice(0, idx))
      + '<mark>' + escapeHtml(text.slice(idx, idx + needle.length)) + '</mark>'
      + escapeHtml(text.slice(idx + needle.length));
  }

  // --- FULL SEARCH VIEW (replaces categories/popular on /pages/help) ------
  async function runFullSearchView(q) {
    const articles = await loadHelpArticles();
    const matches = clientFilter(articles, q);

    // Find or create the results container
    let resultsContainer = document.querySelector('[data-help-search-view]');
    if (!resultsContainer) {
      resultsContainer = document.createElement('section');
      resultsContainer.setAttribute('data-help-search-view', '');
      resultsContainer.className = 'hc-sec';

      // Inject above categories grid on /pages/help, or above article body elsewhere
      const wrap = document.querySelector('[data-help-section="categories"]') ||
                   document.querySelector('[data-help-section="article"]')?.parentElement;
      if (wrap?.parentElement) {
        wrap.parentElement.insertBefore(resultsContainer, wrap);
      } else {
        const mainWrap = document.querySelector('.hc-wrap');
        mainWrap?.prepend(resultsContainer);
      }
    }

    // Hide categories + popular while showing results (only on landing)
    const toHide = document.querySelectorAll(
      '[data-help-section="categories"], [data-help-section="popular"]'
    );
    toHide.forEach((el) => (el.style.display = 'none'));

    resultsContainer.innerHTML = `
      <div class="hc-sec__head">
        <div>
          <h2>${matches.length ? `${matches.length} result${matches.length === 1 ? '' : 's'} for "${escapeHtml(q)}"` : `No results for "${escapeHtml(q)}"`}</h2>
          ${!matches.length ? '<p>Try different keywords, or browse by category below.</p>' : ''}
        </div>
        <a href="${window.location.pathname}" class="hc-sec__link" data-help-clear-search>
          <i data-lucide="x"></i> Clear search
        </a>
      </div>
      ${matches.length ? `
        <div class="hc-arts">
          ${matches.map((a) => `
            <a class="hc-art" href="${escapeHtml(a.url)}">
              <div class="hc-art__icon"><i data-lucide="file-text"></i></div>
              <div class="hc-art__body">
                <div class="hc-art__title">${highlightMatch(a.title, q)}</div>
                ${a.summary ? `<div class="hc-art__excerpt">${escapeHtml(a.summary.slice(0, 120))}</div>` : ''}
              </div>
              <div class="hc-art__chev"><i data-lucide="chevron-right"></i></div>
            </a>
          `).join('')}
        </div>
      ` : `
        <div class="hc-empty">
          <div class="hc-empty__icon"><i data-lucide="search"></i></div>
          <p>Reach out to our team if you can't find what you need.</p>
        </div>
      `}
    `;

    // Wire the clear button
    resultsContainer.querySelector('[data-help-clear-search]')?.addEventListener('click', (e) => {
      e.preventDefault();
      resultsContainer.remove();
      toHide.forEach((el) => (el.style.display = ''));
      // Clear URL ?q=
      window.history.replaceState({}, '', window.location.pathname);
      // Clear all input values
      document.querySelectorAll('[data-help-search-input], [data-help-sticky-search-input]')
        .forEach((i) => (i.value = ''));
    });

    // Scroll the results into view (just below hero)
    resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });

    hydrateIcons(resultsContainer);
  }

  // --- TABLE OF CONTENTS --------------------------------------------------
  function buildTOC() {
    const body = document.querySelector('[data-help-article-body]');
    const tocList = document.querySelector('[data-help-toc-list]');
    const tocNav = document.querySelector('[data-help-toc]');
    if (!body || !tocList || !tocNav) return;

    const headings = body.querySelectorAll('h2');
    if (headings.length < 2) return;

    let html = '';
    headings.forEach((h, i) => {
      if (!h.id) h.id = `hc-h-${i}-${h.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`;
      html += `<li><a href="#${h.id}" data-help-toc-link="${h.id}">${escapeHtml(h.textContent)}</a></li>`;
    });
    tocList.innerHTML = html;
    tocNav.hidden = false;

    // Smooth scroll on click
    tocList.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const id = link.getAttribute('href').slice(1);
        const target = document.getElementById(id);
        if (target) {
          const top = target.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    });

    // Scroll-spy
    setupTOCScrollSpy(headings);
  }

  function setupTOCScrollSpy(headings) {
    const links = document.querySelectorAll('[data-help-toc-link]');
    if (!links.length) return;

    function update() {
      let active = null;
      headings.forEach((h) => {
        const rect = h.getBoundingClientRect();
        if (rect.top <= 120) active = h.id;
      });
      links.forEach((l) => {
        l.classList.toggle('is-active', l.dataset.helpTocLink === active);
      });
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  // --- STICKY HEADER SEARCH REVEAL ----------------------------------------
  function setupStickyHeaderReveal() {
    const stickySearch = document.querySelector('[data-help-sticky-search]');
    if (!stickySearch) return;

    const hero = document.querySelector('[data-help-section="hero"]');
    // No hero on this page (article/category) — always show
    if (!hero) {
      stickySearch.classList.remove('is-hidden');
      return;
    }

    function check() {
      const heroBottom = hero.getBoundingClientRect().bottom;
      stickySearch.classList.toggle('is-hidden', heroBottom >= 80);
    }
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  // --- RICH SCHEMA EMISSION (FAQPage + HowTo) -----------------------------
  // Scans the article body for [data-help-faq] / [data-help-howto] blocks and
  // injects FAQPage / HowTo JSON-LD into <head>. AI crawlers + Google rich
  // results consume these.
  function emitRichSchema() {
    const body = document.querySelector('[data-help-article-body]');
    if (!body) return;
    emitFAQSchema(body);
    emitHowToSchema(body);
  }

  function emitFAQSchema(body) {
    const faqBlock = body.querySelector('[data-help-faq]');
    if (!faqBlock) return;
    const items = [];
    faqBlock.querySelectorAll('details').forEach((d) => {
      const q = d.querySelector('summary');
      const a = d.querySelector('p');
      if (!q || !a) return;
      items.push({
        '@type': 'Question',
        name: q.textContent.trim(),
        acceptedAnswer: { '@type': 'Answer', text: a.textContent.trim() }
      });
    });
    if (!items.length) return;
    injectSchema({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: items });
  }

  function emitHowToSchema(body) {
    const ht = body.querySelector('[data-help-howto]');
    if (!ht) return;
    const name = ht.getAttribute('data-howto-name') || document.querySelector('.hc-article__title')?.textContent.trim();
    const steps = [];
    ht.querySelectorAll('ol li').forEach((li, i) => {
      const strong = li.querySelector('strong');
      const stepName = strong ? strong.textContent.trim().replace(/[.:]$/, '') : `Step ${i + 1}`;
      const text = li.textContent.trim();
      steps.push({
        '@type': 'HowToStep',
        position: i + 1,
        name: stepName,
        text: text
      });
    });
    if (!steps.length) return;
    injectSchema({ '@context': 'https://schema.org', '@type': 'HowTo', name: name, step: steps });
  }

  function injectSchema(obj) {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
  }

  // --- FEEDBACK WIDGET ----------------------------------------------------
  function setupFeedback() {
    const buttons = document.querySelectorAll('[data-help-feedback]');
    if (!buttons.length) return;

    const article = document.querySelector('[data-help-article-id]');
    const articleId = article ? article.dataset.helpArticleId : null;
    const articleHandle = article ? article.dataset.helpArticleHandle : null;
    const actions = document.querySelector('[data-help-feedback-actions]');
    const thanks = document.querySelector('[data-help-feedback-thanks]');

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.helpFeedback;
        sendFeedbackBeacon({ articleId, articleHandle, value });

        // Replace buttons with thank-you
        if (actions) actions.style.display = 'none';
        if (thanks) {
          thanks.hidden = false;
        }

        // If "no", scroll to contact section
        if (value === 'no') {
          const contactSection = document.querySelector('[data-help-section="contact"]');
          if (contactSection) {
            setTimeout(() => contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500);
          }
        }
      });
    });
  }

  function sendFeedbackBeacon(data) {
    try {
      const payload = JSON.stringify({
        ...data,
        page: window.location.pathname,
        timestamp: Date.now()
      });
      if (navigator.sendBeacon) {
        // Future endpoint when ready:
        // navigator.sendBeacon('/apps/help/feedback', payload);
        console.info('[help-centre] feedback:', data);
      }
      try {
        localStorage.setItem(`help_feedback_${data.articleHandle}`, data.value);
      } catch (e) {}
    } catch (e) {}
  }

  // --- CATEGORY ARTICLE COUNTS --------------------------------------------
  function loadCategoryCounts() {
    const counters = document.querySelectorAll('[data-help-category-count]');
    if (!counters.length) return;

    // Server-rendered count map — Liquid emits this in layout/help.liquid
    const dataEl = document.getElementById('help-category-counts-data');
    let counts = {};
    if (dataEl) {
      try {
        counts = JSON.parse(dataEl.textContent) || {};
      } catch (e) {
        console.warn('[help-centre] failed to parse category counts', e);
      }
    }

    counters.forEach((el) => {
      const handle = el.dataset.helpCategoryCount;
      const n = counts[handle] || 0;
      if (n > 0) el.textContent = `${n} article${n === 1 ? '' : 's'}`;
    });
  }

  // --- CHAT INTEGRATION (Printicom) ---------------------------------------
  function setupChatIntegration() {
    document.querySelectorAll('[data-help-chat-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('printicom:open', { detail: { source: 'help-centre' } }));
      });
    });

    window.PrintiblyHelp = window.PrintiblyHelp || {
      searchArticles: async (q, limit = 5) => {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=article&resources[limit]=${limit}`;
        try {
          const res = await fetch(url);
          const data = await res.json();
          return (data.resources && data.resources.results && data.resources.results.articles) || [];
        } catch (e) { return []; }
      },
      getArticleByHandle: async (handle) => {
        try {
          const res = await fetch(`/blogs/help/articles/${handle}.json`);
          if (!res.ok) return null;
          const data = await res.json();
          return data.article || null;
        } catch (e) { return null; }
      }
    };
  }

  // --- UTILITIES ----------------------------------------------------------
  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (s) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[s]);
  }
  function stripTags(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }

  // --- INIT --------------------------------------------------------------
  function init() {
    hydrateIcons();
    setupPredictiveSearch();
    buildTOC();
    setupStickyHeaderReveal();
    setupFeedback();
    loadCategoryCounts();
    setupChatIntegration();
    emitRichSchema();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
